import {Component, Input} from '@angular/core';
import {NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {ConfigService} from '@ngmedax/config';
import {flagIconCodeMap} from '@ngmedax/cloud-translator';

/**
 * Reusable renderer for the origin of a questionnaire. When the thieme feature
 * is licensed it renders a badge ("pill") plus the questionnaire name:
 * - thieme questionnaires: the pill shows the bogencode and the language flag,
 *   parsed from the title prefix "(Code / Locale) Name". A click opens the
 *   thieme catalog filtered to that bogencode. The name is shown without the prefix.
 * - other questionnaires: the pill shows "myMedax" and links to the editor.
 * Without the thieme license it renders the plain title only (no pill), so the
 * rest of the app looks unchanged when thieme is disabled.
 */
@Component({
  selector: 'ngmedax-questionnaire-origin',
  templateUrl: './questionnaire-origin.component.html',
  styleUrls: ['./questionnaire-origin.component.css']
})
export class QuestionnaireOriginComponent {
  /**
   * The questionnaire (needs meta.title, meta.source and id/uid).
   */
  @Input() public questionnaire: any;

  /**
   * Display locale used to pick meta.title.
   */
  @Input() public locale = 'de_DE';

  /**
   * Layout: 'stacked' (pill on top, name below), 'inline' (pill and name in one
   * line), 'badge' (only the pill) or 'name' (only the name, thieme prefix
   * stripped, e.g. where the pill is already shown in another column).
   */
  @Input() public mode: 'stacked' | 'inline' | 'badge' | 'name' = 'stacked';

  /**
   * Whether the questionnaire may be edited (drives the myMedax editor link).
   * When not set, it defaults to "not editable in simu mode" (config mode.simu):
   * consumers that know the exact rule (immutable / simu tags) pass it explicitly.
   */
  @Input() public editable: boolean = null;

  public constructor(private configService: ConfigService) {
  }

  /**
   * True when the thieme feature is licensed (renderer only applies then).
   */
  public get licensed(): boolean {
    return this.configService.get('feature.thieme') === true;
  }

  /**
   * True when the questionnaire originates from thieme. Uses meta.source when
   * available; falls back to the thieme-specific "(Code / Locale)" title prefix
   * (some populate paths, e.g. the survey api, do not carry meta.source).
   */
  public get isThieme(): boolean {
    const meta = this.questionnaire && this.questionnaire.meta;

    if (meta && meta.source && meta.source.provider === 'thieme') {
      return true;
    }

    return this.parseTitle() !== null;
  }

  /**
   * Full title in the display locale (falls back to the first available locale).
   */
  public get title(): string {
    const titles = (this.questionnaire && this.questionnaire.meta && this.questionnaire.meta.title) || {};
    const raw = titles[this.locale] || titles[Object.keys(titles)[0]] || '';
    // strip html tags: template titles may carry formatting markup
    return `${raw}`.replace(/<[^>]*>/g, '');
  }

  /**
   * Bogencode parsed from the title prefix "(Code / Locale) ...", empty when not parseable.
   */
  public get bogenCode(): string {
    const parsed = this.parseTitle();
    return parsed ? parsed.code : '';
  }

  /**
   * Bogen name: the title without the "(Code / Locale)" prefix (full title when not parseable).
   */
  public get bogenName(): string {
    const parsed = this.parseTitle();
    return parsed ? parsed.name : this.title;
  }

  /**
   * Name for the 'name' mode: stripped thieme name when licensed, full title otherwise.
   */
  public get displayName(): string {
    return this.licensed ? this.bogenName : this.title;
  }

  /**
   * Flag-icon country code for the parsed locale, empty when unknown.
   */
  public get flagCode(): string {
    const parsed = this.parseTitle();
    return parsed ? (flagIconCodeMap[parsed.locale] || '') : '';
  }

  /**
   * True when the myMedax pill may link to the editor (explicit input wins,
   * otherwise editing is blocked in simu mode).
   */
  public get canEdit(): boolean {
    if (this.editable !== null) {
      return this.editable;
    }

    return this.configService.get('mode.simu') !== true;
  }

  /**
   * Router link to the thieme catalog (for the thieme pill).
   */
  public get catalogLink(): any[] {
    return ['/module/thieme-catalog/grid'];
  }

  /**
   * Query params for the thieme catalog link (fulltext filter on the bogencode).
   */
  public get catalogQueryParams(): any {
    return this.bogenCode ? {fulltext: this.bogenCode} : {};
  }

  /**
   * Router link to the editor (for the myMedax pill), null when not editable.
   */
  public get editorLink(): any[] {
    if (!this.canEdit) {
      return null;
    }

    const id = this.questionnaire && (this.questionnaire.id || this.questionnaire.uid);
    return id ? ['/module/questionnaire/editor', id] : null;
  }

  /** Measures the rendered title on hover, after layout has settled. */
  public openTitleTooltip(element: HTMLElement, tooltip: NgbTooltip): void {
    if (element.scrollWidth > element.clientWidth) {
      tooltip.open();
    }
  }

  /**
   * Parses the "(Code / Locale) Name" title prefix.
   */
  private parseTitle(): {code: string, locale: string, name: string} {
    const match = /^\(\s*(.+?)\s*\/\s*([a-zA-Z]{2}_[a-zA-Z]{2})\s*\)\s*(.*)$/.exec(this.title);

    if (!match) {
      return null;
    }

    return {code: match[1], locale: match[2], name: match[3]};
  }
}
